import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Gift Cards — dedicated retail page.
 *
 * Flow (as agreed with the studio):
 *  1. Sender picks an amount ($25 / $50 / $100 / $200) or a custom amount.
 *  2. Sender fills recipient details + optional message.
 *  3. POST /api/gift-cards/purchase creates a pending record and emails the
 *     studio + sender. The recipient's code is NOT emailed until an admin
 *     manually marks the gift card as paid — kept simple, no live payments.
 */
export default function GiftCards() {
  const [settings, setSettings] = useState({ presets: [25, 50, 100, 200], min_amount: 10, max_amount: 500 });
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/gift-cards/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const selectedAmount = customMode
    ? Math.max(settings.min_amount, Math.min(settings.max_amount, Number(customAmount) || 0))
    : amount;

  const canSubmit =
    selectedAmount >= settings.min_amount &&
    selectedAmount <= settings.max_amount &&
    recipientName.trim() &&
    recipientEmail.trim() &&
    senderName.trim() &&
    senderEmail.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gift-cards/purchase", {
        amount: selectedAmount,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim(),
        sender_name: senderName.trim(),
        sender_email: senderEmail.trim(),
        message: message.trim(),
      });
      setDone(data);
      toast.success("Gift card request received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit gift card request.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(null);
    setAmount(50);
    setCustomMode(false);
    setCustomAmount("");
    setRecipientName("");
    setRecipientEmail("");
    setSenderName("");
    setSenderEmail("");
    setMessage("");
  };

  return (
    <div data-testid="gift-cards-page" className="fade-up">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-6 text-center">
        <p className="overline">Gift Cards</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl text-brand-dark mt-4 leading-[1.05]">
          Share the <span className="italic">love</span>.
        </h1>
        <p className="mt-5 text-brand-neutral max-w-2xl mx-auto leading-relaxed">
          For bespoke illustrations, custom stationery, original artwork — or simply to let them
          choose their favourite greeting cards or limited-edition prints. Select an amount,
          include a personal message, and we'll deliver your gift card straight to their inbox.
        </p>
      </section>

      {/* Hero image — sized more modestly so the purchase form takes priority */}
      <section className="max-w-[720px] mx-auto px-6 md:px-12 pt-4 pb-14">
        <div className="overflow-hidden">
          <img
            data-testid="gift-card-hero"
            src="/brand/gift-card.png"
            alt="The Island Studio gift card featuring a hand holding pink bougainvillea"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>

      {/* Confirmation state */}
      {done ? (
        <section data-testid="gift-purchase-done" className="max-w-[720px] mx-auto px-6 md:px-12 pb-24">
          <div className="border border-brand-border p-10 md:p-14 text-center bg-[#F4F2EC]">
            <p className="overline text-brand-accent">Request received</p>
            <h2 className="font-serif font-light text-3xl md:text-4xl text-brand-dark mt-4">
              Thank you.
            </h2>
            <p className="mt-4 text-brand-neutral leading-relaxed">
              Your gift card for <b className="text-brand-dark">${done.amount.toFixed(2)}</b> has
              been queued for <b className="text-brand-dark">{recipientName || "your recipient"}</b>.
              A confirmation has just been sent to your inbox.
            </p>
            <p className="mt-3 text-sm text-brand-neutral">
              Reference: <span className="font-mono text-brand-dark">{done.reference}</span>
            </p>
            <p className="mt-6 text-sm text-brand-neutral leading-relaxed max-w-md mx-auto">
              We'll be in touch shortly with bank transfer details. Once payment is received,
              your recipient will get their unique gift card code — along with your personal
              message.
            </p>
            <button
              data-testid="gift-purchase-another"
              onClick={reset}
              className="btn-outline mt-8"
            >
              Send another gift card
            </button>
          </div>
        </section>
      ) : (
        <section className="max-w-[720px] mx-auto px-6 md:px-12 pb-24">
          <form onSubmit={onSubmit} className="space-y-12">
            {/* Amount picker */}
            <div>
              <p className="overline">1. Choose an amount</p>
              <div
                data-testid="gift-amounts"
                className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-3"
              >
                {settings.presets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`gift-amount-${p}`}
                    onClick={() => {
                      setCustomMode(false);
                      setAmount(p);
                    }}
                    className={`h-20 flex items-center justify-center border text-center transition-colors ${
                      !customMode && amount === p
                        ? "border-brand-accent text-brand-accent bg-[#F4F2EC]"
                        : "border-brand-border text-brand-dark hover:border-brand-dark"
                    }`}
                  >
                    <span className="font-serif text-2xl leading-none">${p.toFixed(0)}</span>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="gift-amount-custom"
                  onClick={() => setCustomMode(true)}
                  className={`h-20 flex items-center justify-center border text-center transition-colors ${
                    customMode
                      ? "border-brand-accent text-brand-accent bg-[#F4F2EC]"
                      : "border-brand-border text-brand-dark hover:border-brand-dark"
                  }`}
                >
                  <span className="font-serif text-2xl leading-none">Custom</span>
                </button>
              </div>

              {customMode && (
                <div className="mt-6 max-w-xs">
                  <label className="block">
                    <span className="overline block mb-1">Custom amount ($)</span>
                    <input
                      data-testid="gift-custom-amount"
                      type="number"
                      min={settings.min_amount}
                      max={settings.max_amount}
                      step="1"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="flushed-input"
                      placeholder={`${settings.min_amount}–${settings.max_amount}`}
                    />
                  </label>
                  <p className="text-xs text-brand-neutral mt-2">
                    Between ${settings.min_amount} and ${settings.max_amount}.
                  </p>
                </div>
              )}
            </div>

            {/* Recipient */}
            <div>
              <p className="overline">2. Who's it for?</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline block mb-1">Recipient name</span>
                  <input
                    data-testid="gift-recipient-name"
                    required
                    className="flushed-input"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-1">Recipient email</span>
                  <input
                    data-testid="gift-recipient-email"
                    required
                    type="email"
                    className="flushed-input"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {/* Sender */}
            <div>
              <p className="overline">3. From you</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline block mb-1">Your name</span>
                  <input
                    data-testid="gift-sender-name"
                    required
                    className="flushed-input"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-1">Your email</span>
                  <input
                    data-testid="gift-sender-email"
                    required
                    type="email"
                    className="flushed-input"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                  />
                </label>
              </div>
              <label className="block mt-6">
                <span className="overline block mb-1">A personal message (optional)</span>
                <textarea
                  data-testid="gift-message"
                  rows={4}
                  className="flushed-input resize-none"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a note that will appear inside their gift card email…"
                />
              </label>
            </div>

            {/* Submit */}
            <div className="border-t border-brand-border pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="overline">Total</p>
                <p
                  data-testid="gift-total"
                  className="font-serif text-3xl md:text-4xl text-brand-dark mt-1"
                >
                  ${selectedAmount ? selectedAmount.toFixed(2) : "0.00"}
                </p>
                <p className="text-xs text-brand-neutral mt-2 max-w-md leading-relaxed">
                  You'll receive a confirmation email; the studio will follow up with bank
                  transfer details. The recipient receives their code once payment is confirmed.
                </p>
              </div>
              <button
                data-testid="gift-purchase-submit"
                type="submit"
                disabled={!canSubmit || busy}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Sending…" : "Send Gift Card"}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
